/** RFC 0345. Use a dedicated store for Keyhive identity/state, separate from
 * Automerge document storage. The host persists the wrapping key in its
 * keychain (desktop) or as a non-extractable CryptoKey in IndexedDB (browser).
 */
import type { StorageAdapterInterface } from '@automerge/automerge-repo/slim'

type StorageKey = Parameters<StorageAdapterInterface['load']>[0]
const encoder = new TextEncoder()
const VERSION = 1

export class PrivateKeyhiveStorage implements StorageAdapterInterface {
  constructor(
    private readonly storage: StorageAdapterInterface,
    private readonly key: CryptoKey,
  ) {
    if (
      key.type !== 'secret' ||
      key.algorithm.name !== 'AES-GCM' ||
      !key.usages.includes('encrypt') ||
      !key.usages.includes('decrypt')
    )
      throw new Error('Keyhive storage requires an AES-GCM wrapping key')
  }

  /**
   * Each record is bound to its key in the form a store reads it back. Keyhive
   * writes keys with segments like "/archives/", and a filesystem store
   * rebuilds keys from file paths, so it returns "archives": bound to the key
   * as written, every record failed to open once the daemon restarted. A key
   * without such segments binds exactly as before.
   */
  private binding(path: StorageKey): Uint8Array<ArrayBuffer> {
    const canonical = path.flatMap((segment) =>
      segment.split('/').filter((part) => part.length > 0),
    )
    return encoder.encode(JSON.stringify(['mod:keyhive-storage:v1', canonical]))
  }

  /** The binding before keys were canonical: the key as written. A browser
   * store returns keys verbatim, so its older records still open with it. */
  private legacyBinding(path: StorageKey): Uint8Array<ArrayBuffer> {
    return encoder.encode(JSON.stringify(['mod:keyhive-storage:v1', path]))
  }

  private async seal(path: StorageKey, value: Uint8Array): Promise<Uint8Array> {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encrypted = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, additionalData: this.binding(path) },
        this.key,
        new Uint8Array(value),
      ),
    )
    const result = new Uint8Array(13 + encrypted.length)
    result[0] = VERSION
    result.set(iv, 1)
    result.set(encrypted, 13)
    return result
  }

  private async open(path: StorageKey, value: Uint8Array): Promise<Uint8Array> {
    if (value.length < 29 || value[0] !== VERSION)
      throw new Error('Invalid encrypted Keyhive storage record')
    const decrypt = async (additionalData: Uint8Array<ArrayBuffer>) =>
      new Uint8Array(
        await crypto.subtle.decrypt(
          {
            name: 'AES-GCM',
            iv: new Uint8Array(value.slice(1, 13)),
            additionalData,
          },
          this.key,
          new Uint8Array(value.slice(13)),
        ),
      )
    try {
      return await decrypt(this.binding(path))
    } catch (error) {
      // Only a key with "/" in a segment binds differently the old way.
      if (!path.some((segment) => segment === '' || segment.includes('/')))
        throw error
      return decrypt(this.legacyBinding(path))
    }
  }

  async load(path: StorageKey): Promise<Uint8Array | undefined> {
    const value = await this.storage.load(path)
    return value === undefined ? undefined : this.open(path, value)
  }

  async save(path: StorageKey, value: Uint8Array): Promise<void> {
    await this.storage.save(path, await this.seal(path, value))
  }

  async loadRange(prefix: StorageKey) {
    return Promise.all(
      (await this.storage.loadRange(prefix)).map(async (chunk) => ({
        key: chunk.key,
        data:
          chunk.data === undefined
            ? undefined
            : await this.open(chunk.key, chunk.data),
      })),
    )
  }

  async saveBatch(entries: Array<[StorageKey, Uint8Array]>): Promise<void> {
    // Finish every encryption before invoking the underlying staged batch.
    const encrypted = await Promise.all(
      entries.map(
        async ([path, value]) =>
          [path, await this.seal(path, value)] as [StorageKey, Uint8Array],
      ),
    )
    await this.storage.saveBatch(encrypted)
  }

  remove(path: StorageKey) {
    return this.storage.remove(path)
  }
  removeRange(prefix: StorageKey) {
    return this.storage.removeRange(prefix)
  }
  close() {
    return this.storage.close?.()
  }
}
